/**
 * lazyWithRetry — wrapper autour de React.lazy() avec retry automatique
 * =====================================================================
 *
 * Problème ciblé : après un nouveau déploiement, un onglet déjà ouvert peut
 * tenter de charger un chunk JS (lazy import) qui a été remplacé/nettoyé par
 * Vercel. Le premier essai échoue, mais souvent un retry quelques centaines
 * de ms plus tard fonctionne (le CDN propage la nouvelle version, ou le
 * service worker met à jour son cache).
 *
 * Si après plusieurs tentatives ça échoue toujours, on relance l'erreur
 * d'origine — `ErrorBoundary` la détecte comme ChunkLoadError et déclenche
 * un reload de la page complète (cohérence HTML + bundles).
 *
 * Usage :
 *   const Component = lazyWithRetry(() => import('./Component'));
 *
 * Équivalent direct à `React.lazy(...)` côté API.
 */
import { lazy } from 'react';

const DEFAULT_RETRIES = 2;
const RETRY_DELAY_MS = 500;

export const lazyWithRetry = (importFn, retries = DEFAULT_RETRIES) => {
  return lazy(async () => {
    let lastError = null;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await importFn();
      } catch (error) {
        lastError = error;
        // Si ce n'est pas une erreur de chargement de chunk, on ne retry pas
        // (ce serait un vrai bug applicatif à laisser remonter).
        const msg = String(error?.message || error || '');
        const name = String(error?.name || '');
        const isChunkErr = (
          name === 'ChunkLoadError'
          || /Loading chunk \S+ failed/i.test(msg)
          || /Loading CSS chunk \S+ failed/i.test(msg)
          || /Failed to fetch dynamically imported module/i.test(msg)
          || /Importing a module script failed/i.test(msg)
          || /Refused to execute .* as script/i.test(msg)
        );
        if (!isChunkErr) throw error;
        if (attempt === retries) break;
        // Backoff progressif : 500ms, 1000ms…
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)));
      }
    }
    // Toutes les tentatives ont échoué — relance l'erreur d'origine
    // (ErrorBoundary va l'attraper et déclencher un reload).
    throw lastError;
  });
};

export default lazyWithRetry;
